import { useMemo, useState } from 'react';

import { AUDIT_PAGE_SIZE, useAudit, useUsers, type AuditListQuery } from '../../api/admin';
import { Badge, Button, EmptyState, ErrorState, Input, Select } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { formatDateTime } from '../dashboard/format';
import {
  auditUserLabel,
  clampPage,
  compactMeta,
  pageCount,
  parseAuditPage,
  parseUsers,
  userLabel,
} from './settings-model';

/**
 * И8 — the audit log (contract #24, ADMIN-only, task T.C5).
 *
 * The security trail (`AuditLog`), NOT the application timeline — the tab keeps
 * the wording («журнал аудита») and the columns the contract offers: who, what,
 * on which entity, with the free-form `meta` as one compact JSON line.
 *
 * Both filters are SERVER-side (the contract takes `?entity=`/`?userId=`), so a
 * filter change is a fresh read and the pager always moves inside the filtered
 * total. `meta` is only ever truncated for display — the full JSON is in the
 * cell's `title`, where a copy still finds it.
 */
export function AuditLogTab() {
  const [entity, setEntity] = useState('');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);

  const query: AuditListQuery = useMemo(
    () => ({ entity: entity.trim() === '' ? undefined : entity.trim(), userId, page, pageSize: AUDIT_PAGE_SIZE }),
    [entity, userId, page],
  );
  const audit = useAudit(query);
  const users = useUsers();

  const userOptions = useMemo(() => parseUsers(users.data), [users.data]);
  const { rows, meta } = useMemo(() => parseAuditPage(audit.data, query), [audit.data, query]);
  const pages = pageCount(meta.total, meta.pageSize);
  // A shrinking filter can leave the URL on a page that no longer exists; the
  // pager then renders the clamped position instead of an empty table.
  const currentPage = clampPage(meta.page, pages);

  const pickEntity = (next: string) => {
    setEntity(next);
    setPage(1);
  };
  const pickUser = (next: number | undefined) => {
    setUserId(next);
    setPage(1);
  };

  return (
    <div className="card card--flush" data-testid="audit-panel">
      <div className="settings-toolbar">
        <div className="settings-toolbar__field">
          <label className="field__label" htmlFor="audit-entity">
            Сущность
          </label>
          <Input
            id="audit-entity"
            data-testid="filter-entity"
            list="audit-entity-options"
            placeholder="напр. user"
            value={entity}
            onChange={(event) => setEntity(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            onBlur={(event) => pickEntity(event.target.value)}
          />
          <datalist id="audit-entity-options">
            {entitiesSeen(rows).map((candidate) => (
              <option key={candidate} value={candidate} />
            ))}
          </datalist>
        </div>

        <div className="settings-toolbar__field">
          <label className="field__label" htmlFor="audit-user">
            Пользователь
          </label>
          <Select
            id="audit-user"
            data-testid="filter-user"
            value={userId ?? ''}
            onChange={(event) => pickUser(event.target.value === '' ? undefined : Number(event.target.value))}
          >
            <option value="">Все пользователи</option>
            {userOptions.map((user) => (
              <option key={user.id} value={user.id}>
                {userLabel(user)}
              </option>
            ))}
          </Select>
        </div>

        <p className="settings-toolbar__meta" data-testid="audit-meta">
          {meta.total} записей · по {meta.pageSize || AUDIT_PAGE_SIZE} на страницу
        </p>
      </div>

      {audit.isPending ? (
        <div className="settings-skeleton" data-testid="audit-skeleton">
          <div className="skeleton skeleton--line skeleton--wide" />
          <div className="skeleton skeleton--line" />
          <div className="skeleton skeleton--line" />
        </div>
      ) : audit.isError ? (
        <div className="settings-skeleton">
          <ErrorState
            message={audit.error instanceof Error ? audit.error.message : 'Не удалось загрузить журнал.'}
            onRetry={() => void audit.refetch()}
          />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Записей нет"
          hint={
            entity.trim() !== '' || userId !== undefined
              ? 'По этим фильтрам записей нет — ослабьте условие.'
              : 'Журнал наполняется действиями: входами, экспортами и изменениями сущностей.'
          }
        />
      ) : (
        <>
          <TableWrap>
            <table className="data" data-testid="audit-table">
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Действие</th>
                  <th scope="col">Сущность</th>
                  <th scope="col">Идентификатор</th>
                  <th scope="col">Пользователь</th>
                  <th scope="col">Детали</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} data-testid="audit-row" data-action={row.action} data-entity={row.entity}>
                    <td data-testid="audit-when">{formatDateTime(row.createdAt)}</td>
                    <td data-testid="audit-action">
                      <Badge tone={actionTone(row.action)}>{row.action}</Badge>
                    </td>
                    <td data-testid="audit-entity">{row.entity}</td>
                    <td data-testid="audit-entity-id" className="num">
                      {row.entityId ?? '—'}
                    </td>
                    <td data-testid="audit-user">{auditUserLabel(row.userId, userOptions)}</td>
                    <td
                      data-testid="audit-meta-cell"
                      className="audit-meta"
                      title={row.meta ? safeJson(row.meta) : undefined}
                    >
                      {compactMeta(row.meta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>

          <div className="pagination" data-testid="audit-pagination">
            <span data-testid="audit-page-label">
              Страница {currentPage} из {pages}
            </span>
            <span className="pagination__pages">
              <Button size="sm" data-testid="page-prev" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                ← Назад
              </Button>
              <Button
                size="sm"
                data-testid="page-next"
                disabled={currentPage >= pages}
                onClick={() => setPage(currentPage + 1)}
              >
                Вперёд →
              </Button>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/** The entities currently on screen — the `datalist` suggestions. */
function entitiesSeen(rows: ReadonlyArray<{ entity: string }>): string[] {
  return [...new Set(rows.map((row) => row.entity))].sort();
}

/** LOGIN/EXPORT are read-only trail entries; a change is the one worth a colour. */
function actionTone(action: string): 'ok' | 'brand' | 'warn' {
  if (action === 'ENTITY_CHANGE') return 'warn';
  if (action === 'EXPORT') return 'brand';
  return 'ok';
}

/** `meta` for the `title` attribute — a malformed row must not break the table. */
function safeJson(meta: Record<string, unknown>): string {
  try {
    return JSON.stringify(meta);
  } catch {
    return String(meta);
  }
}
